// v186 SystemBanner — strip rendered above the topbar in AppShell /
// AdminShell when /api/public-config returns an active_banner.
//
// Behaviour:
//   * Severity drives the colour (info/warning/critical).
//   * Dismissible banners persist their dismissal in localStorage
//     keyed by banner id, so re-publishing under a fresh id
//     re-shows the banner to everyone.
//   * Non-dismissible banners have no close button.
//   * Banner re-fetched every minute via usePublicConfig's SWR
//     interval; ends_at expiry on the backend means the banner just
//     stops being returned, and the strip disappears at next poll.

import { useEffect, useState } from 'react';
import { Megaphone, AlertTriangle, Info, X } from 'lucide-react';
import type { ActiveBanner } from '../api/publicConfig';

const DISMISSED_KEY_PREFIX = 'orbital.banner.dismissed.';

function loadDismissed(id: string): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY_PREFIX + id) === '1';
  } catch {
    return false;
  }
}

function saveDismissed(id: string) {
  try {
    localStorage.setItem(DISMISSED_KEY_PREFIX + id, '1');
  } catch {
    /* localStorage disabled — banner just won't stay dismissed */
  }
}

const SEVERITY_CLASSES: Record<ActiveBanner['severity'], { wrap: string; icon: string }> = {
  info: {
    wrap: 'border-accent/30 bg-accent/10',
    icon: 'text-accent',
  },
  warning: {
    wrap: 'border-warning/30 bg-warning/10',
    icon: 'text-warning',
  },
  critical: {
    wrap: 'border-danger/40 bg-danger/10',
    icon: 'text-danger',
  },
};

function severityIcon(severity: ActiveBanner['severity']) {
  if (severity === 'critical') return AlertTriangle;
  if (severity === 'warning') return Megaphone;
  return Info;
}

export function SystemBanner({ banner }: Readonly<{ banner: ActiveBanner }>) {
  const [dismissed, setDismissed] = useState(false);

  // Re-evaluate dismissal whenever the banner ID changes — operators
  // re-publishing under a fresh row must reach users who dismissed
  // the previous one.
  useEffect(() => {
    setDismissed(loadDismissed(banner.id));
  }, [banner.id]);

  if (dismissed) return null;

  const cls = SEVERITY_CLASSES[banner.severity];
  const Icon = severityIcon(banner.severity);

  return (
    <div className={'border-b ' + cls.wrap}>
      <div className="px-6 py-2.5 flex items-start gap-3 text-sm">
        <Icon className={'h-4 w-4 mt-0.5 shrink-0 ' + cls.icon} />
        <div className="flex-1 min-w-0">
          <span className="font-semibold text-fg">{banner.title}</span>
          {banner.body && (
            <span className="text-fg-muted ml-2 whitespace-pre-wrap break-words">
              {banner.body}
            </span>
          )}
        </div>
        {banner.dismissible && (
          <button
            type="button"
            onClick={() => {
              saveDismissed(banner.id);
              setDismissed(true);
            }}
            className="text-fg-muted hover:text-fg transition-colors shrink-0"
            aria-label="Dismiss banner"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
